import {KpisList} from '@domain/kpis'
import {HierarchyNodeType} from '@domain/shared'
import {tabIndexFiltered, useKpiValue} from '@features/performance-reports'
import {DataContentWrapper} from '@hconnect/common/containers'
import {useQueryParamValue} from '@hconnect/common/hooks'
import {PageContainer} from '@hconnect/uikit/src/lib2'
import {Box, Paper, Theme, useMediaQuery} from '@mui/material'
import React, {useEffect, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {generatePath, useLocation, useNavigate, useParams} from 'react-router'

import {handleError} from '../../shared/helpers'
import {useNavAccessContext} from '../../shared/hooks/useNavAccessContext'
import {TimeRange} from '../../shared/types'
import {CommentDialog} from '../components/CommentDialog'
import {DateTimeRangePicker} from '../components/DateTimeRangePicker'
import {KpiTabs} from '../components/KpiTabs'
import {PERFORMANCE_TAB} from '../consts'
import {AcVsPlContainer} from '../containers/AcVsPlContainer'
import {MobileAcVsPlContainer} from '../containers/MobileAcVsPlContainer'
import {StatusPageHeader} from '../containers/StatusPageHeader'
import {filterCommentsByLocation} from '../helpers'
import {useComments} from '../hooks/useComments'
import {useHierarchyData} from '../hooks/useHierarchy'
import {useTimeRange} from '../hooks/useTimeRange'
import {useTranslationPrefix} from '../hooks/useTranslationPrefix'
import {AREA_KPI, PERFORMANCE} from '../routing'
import {CommentLocationData} from '../types'

type PathParameter = {
  locationId?: string
}

type LocationsId = Partial<{
  areaId: string
  regionId: string
  countryId: string
}>
const pathToLocationsId = (path: string): LocationsId => {
  const [areaId, regionId, countryId] = path.split('.')
  return {areaId, regionId, countryId}
}

export const ActualVsPlanned: React.FC = () => {
  const {t} = useTranslation()
  const {performancePrefix} = useTranslationPrefix()
  const {locationId} = useParams<PathParameter>()
  const {from, to, quickSelectionId} = useTimeRange()
  const {search} = useLocation()
  const navigate = useNavigate()
  const dateRange: TimeRange = {startDate: from.toJSON(), endDate: to.toJSON()}
  const {allowMultiplePlants} = useNavAccessContext()

  const hierarchy = useHierarchyData()

  const [currentTab = KpisList.ClinkerProduction, setCurrentTab] = useQueryParamValue<KpisList>(
    PERFORMANCE_TAB,
    {
      keepInLocalStorage: false
    }
  )
  const [commentLocationData, setCommentLocationData] = React.useState<
    CommentLocationData | undefined
  >(undefined)

  const {data: comments = []} = useComments(
    {
      timeRange: dateRange,
      locationId: locationId || HierarchyNodeType.Global
    },
    {
      select: (data) => data.filter((comment) => comment.topic === currentTab)
    }
  )

  const {data, hasQuarterlyData, isInitialLoading, error, refetch} = useKpiValue({
    currentTab,
    from: dateRange.startDate,
    to: dateRange.endDate,
    quickSelectionId,
    locationId
  })

  useEffect(() => {
    if (error) {
      handleError(error, navigate)
    }
  }, [error, navigate])

  const backButtonProps = useMemo(() => {
    const locationsId = data?.locationData?.path
      ? pathToLocationsId(data.locationData.path)
      : undefined
    if (locationsId?.areaId && (locationsId.countryId || locationsId.regionId)) {
      return {
        targetName: t(`${performancePrefix}.goBackLabels.backToArea`),
        target: generatePath(AREA_KPI, {locationId: locationsId.areaId}) + search
      }
    }
    if (allowMultiplePlants() && locationId) {
      return {
        targetName: t(`${performancePrefix}.goBackLabels.backToGlobal`),
        target: PERFORMANCE + search
      }
    }
  }, [allowMultiplePlants, data?.locationData?.path, search, locationId, t, performancePrefix])

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))

  return (
    <PageContainer>
      <Box pb={2}>
        <StatusPageHeader
          filterComponent={<DateTimeRangePicker type="performance" />}
          countryCode={data?.locationData?.countryCode}
          title={
            isInitialLoading
              ? ''
              : data?.locationData?.name || t(`${performancePrefix}.global.pageTitle`)
          }
          backButtonProps={backButtonProps}
        />
      </Box>

      <DataContentWrapper
        showLoader={isInitialLoading}
        retryFunction={refetch}
        isError={!!error}
        data={data}
      >
        {data &&
          (isMobile ? (
            <Box data-test-id="ac-vs-pl-page">
              <KpiTabs currentTab={currentTab} setCurrentTab={setCurrentTab} />
              <MobileAcVsPlContainer
                data={data}
                hasQuarterlyData={hasQuarterlyData}
                currentTab={currentTab}
                hideCharts={!tabIndexFiltered.includes(currentTab)}
                setCommentLocationData={setCommentLocationData}
                comments={comments}
              />
            </Box>
          ) : (
            <Paper sx={{padding: 2}} data-test-id="ac-vs-pl-page">
              <KpiTabs currentTab={currentTab} setCurrentTab={setCurrentTab} />
              <AcVsPlContainer
                data={data}
                hasQuarterlyData={hasQuarterlyData}
                currentTab={currentTab}
                hideCharts={!tabIndexFiltered.includes(currentTab)}
                setCommentLocationData={setCommentLocationData}
                comments={comments}
              />
            </Paper>
          ))}
      </DataContentWrapper>

      {commentLocationData && (
        <CommentDialog
          topic={currentTab}
          handleClose={() => setCommentLocationData(undefined)}
          commentLocationData={commentLocationData}
          hierarchy={hierarchy}
          comments={
            hierarchy
              ? filterCommentsByLocation(comments, hierarchy, commentLocationData.locationData.id)
              : []
          }
        />
      )}
    </PageContainer>
  )
}
